import { HeaderCellDefaultTable } from "./defaultPlanningTableComponents/HeaderCellDefaultTable";
import { DataCellDefaultTable } from "./defaultPlanningTableComponents/DataCellDefaultTable";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { ConfrimModal } from "../ConfrimModal";
import {
    PRESET_COLUMN_IDS,
    buildForecastVsActualsColumnIds,
    buildMonthlyTrendColumnIds,
    getDemandPlanningColumnLabel,
    defaultWidthForColumn,
    formatPlanningCell,
    LEFT_COLUMN_WIDTHS,
    dedupeDemandPlanningColumnIds,
    MONTH_TREND_MONTH_PREFIXES,
    viewUsesSkuAllocationDerivedChecks,
} from "../../consts/demandplanning";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    clearDemandPlanningTableData,
    DemandPlanningLoadingKeys,
    getEligibilityValidationTableData,
    getForecastBySkuTableData,
    getForecastVsActualsTableData,
    getHistoricalTableData,
    getMonthlyTrendTableData,
    getOpenToBuyTableData,
    getSkuAllocationTableData,
    saveForecastDemandProductApproval,
    saveForecastDemandProductApprovedNotes,
    saveForecastDemandProductOverride,
    getOverstockInventoryTableData,
} from "../../slices/demandPlanningSlice";
import { LoaderSpinner } from "../LoaderSpinner";
import { arrayHasItems } from "../../helpers/common";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ProductCatalogPageSizeSelect } from "../productCatalog/ProductCatalogPageSizeSelect";
import InventoryStatusChip from "../InventoryStatusChip";
import {
    deriveSkuAllocationChecks,
    subcategoryEligibleTotalsMapFromTablePayload,
    computeEligibleFinalPercent,
    toEligibleAggregateMicro,
    subcategoryEligibleTotalFromMicro,
    PERCENT_AGGREGATE_SCALE,
} from "../../lib/skuAllocationDerivedChecks";
import { filterDemandPlanningTableRecordsBySearch } from "../../lib/demandPlanningTableSearch";
import { SelectDropdown } from "../SelectDropdown";
import ProductCatalogBulkFieldMenu from "../productCatalog/ProductCatalogBulkFieldMenu";
import SkuAllocationHistoryDrawer from "./SkuAllocationHistoryDrawer";

const VALIDATION_COLUMNS = new Set([
    "allocationTotalCheck",
    "forecastableCheck",
    "overrideConflictCheck",
    "forecastInputCheck",
]);

const riskTone = (value) => {
    if (["HEALTHY", "OK", "TRUE"].includes(value)) {
      return "border-transparent bg-emerald-50 text-emerald-700";
    }
    if (["WATCH", "ELEVATED"].includes(value)) {
      return "border-transparent bg-amber-50 text-amber-700";
    }
    if (["BUY", "ACTION", "CRITICAL", "STRUCTURAL", "OVERSTOCK", "STOCKOUT RISK", "EXCLUDED"].includes(value)) {
      return "border-transparent bg-red-50 text-red-700";
    }
    return "border-transparent bg-slate-100 text-slate-700";
};

const percentValuesEqual = (a, b) => {
    const norm = (v) =>
        v == null || v === "" || (typeof v === "number" && Number.isNaN(v)) ? null : Number(v);
    const na = norm(a);
    const nb = norm(b);
    if (na == null && nb == null) return true;
    if (na == null || nb == null) return false;
    return Math.abs(na - nb) < 1e-9;
};

const normalizeApprovedSelectValue = (value) => {
    if (value === true || String(value).toLowerCase() === "true") return "true";
    if (value === false || String(value).toLowerCase() === "false") return "false";
    return "";
};

const BOOLEAN_COLUMNS = new Set([
    "forecastable",
    "orderEligible",
    "procurementEligible",
    "needsReview",
]);
const MANUAL_PERCENT_COLUMNS = new Set(["overridePercent", "newProductOverride"]);

const DEFAULT_PLANNING_PAGE_SIZE = 100;
const PLANNING_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/** sku-allocation: monthly + recalc total — headers use prior calendar year (planning year − 1). */
const SKU_ALLOCATION_MONTHLY_REVENUE_IDS = [
    "janRevenue",
    "febRevenue",
    "marRevenue",
    "aprRevenue",
    "mayRevenue",
    "junRevenue",
    "julRevenue",
    "augRevenue",
    "sepRevenue",
    "octRevenue",
    "novRevenue",
    "decRevenue",
];
const SKU_ALLOCATION_MONTH_SHORT = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
const SKU_ALLOCATION_RECALC_TOTAL_COLUMN_ID = "recalcTotal2025";

const getSkuAllocationMonthlyRevenueHeaderLabel = (columnId, demandYear) => {
    if (!Number.isFinite(demandYear)) return null;
    if (columnId === SKU_ALLOCATION_RECALC_TOTAL_COLUMN_ID) {
        return `Recalc Total ${demandYear}`;
    }
    const idx = SKU_ALLOCATION_MONTHLY_REVENUE_IDS.indexOf(columnId);
    if (idx === -1) return null;
    return `${SKU_ALLOCATION_MONTH_SHORT[idx]} ${demandYear}`;
};

const parsePercentInput = (value) => {
    const trimmed = String(value || "").trim().replace(/%/g, "");
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) return null;
    return parsed / 100;
};

const formatPercentInputValue = (value) => {
    if (value === null || typeof value === "undefined") return "";
    const formatted = (Number(value) * 100)
        .toFixed(2)
        .replace(/\.00$/, "")
        .replace(/(\.\d*[1-9])0+$/, "$1");
    return formatted;
};

/** Allow only digits and at most one decimal point (paste/key/filter). */
const sanitizeNumericPercentDraft = (raw) => {
    const core = String(raw ?? "").replace(/%/g, "");
    let out = "";
    let hasDot = false;
    for (const ch of core) {
        if (ch >= "0" && ch <= "9") {
            out += ch;
        } else if (ch === "." && !hasDot) {
            hasDot = true;
            out += ch;
        }
    }
    return out;
};

const PercentEntryInput = ({ value, onCommit }) => {
    const [draft, setDraft] = useState(formatPercentInputValue(value));

    useEffect(() => {
        setDraft(formatPercentInputValue(value));
    }, [value]);

    const commitDraft = () => {
        const next = parsePercentInput(draft);
        setDraft(formatPercentInputValue(next));
        onCommit(next);
    };

    return (
        <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="h-8 w-[120px] rounded-md border border-gray-200 bg-white px-2 text-right text-xs shadow-none focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300"
            onBlur={commitDraft}
            onChange={(event) => setDraft(sanitizeNumericPercentDraft(event.target.value))}
            onFocus={(event) => event.currentTarget.select()}
            onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
            }}
            placeholder="%"
            value={draft}
        />
    );
};

const statusTone = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "ACTIVE") return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    if (normalized === "PLANNED") return "bg-blue-50 text-blue-700 border border-blue-200";
    if (normalized === "SUNSET") return "bg-amber-50 text-amber-700 border border-amber-200";
    if (normalized === "IN_ACTIVE") return "bg-rose-50 text-rose-700 border border-rose-200";
    return "bg-gray-100 text-gray-700 border border-gray-200";
};

const validationTone = (value) => {
    const normalized = String(value || "").toUpperCase();
    if (normalized === "OK") return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    if (
        normalized === "ERROR" || 
        normalized === "MISSING PRICE" ||
        normalized === "NO FORECAST REVENUE" || 
        normalized === "PRICE = 0" || 
        normalized === "UNITS = 0"
    ) return "bg-rose-50 text-rose-700 border border-rose-200";
    return "bg-gray-100 text-gray-700 border border-gray-200";
};

const DefaultPlanningTable = ({
    activeView,
    searchTerm = "",
    records: recordsProp,
    visibleColumnIds: visibleColumnIdsProp,
    selectedForecastDemandPlanning,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
    overrideResetEpoch = 0,
    historyDrawerOpen = false,
    onHistoryDrawerOpenChange,
    marginFromSidebar,
}) => {
    const headerCheckboxRef = useRef(null);
    const [selectedSkuIds, setSelectedSkuIds] = useState([]);
    const [manualPercentById, setManualPercentById] = useState({});
    const tableScrollRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PLANNING_PAGE_SIZE);
    const [bulkApprovalConfirmOpen, setBulkApprovalConfirmOpen] = useState(false);
    const [bulkApprovalSaving, setBulkApprovalSaving] = useState(false);
    const [pendingBulkApproval, setPendingBulkApproval] = useState(null);
    const dispatch = useDispatch();

    const {
        loadingKeys,
        skuAllocationTableData,
        eligibilityValidationTableData,
        monthlyTrendTableData,
        historicalTableData,
        forecastVsActualsTableData,
        forecastBySkuTableData,
        openToBuyTableData,
        overstockInventoryTableData,
        firstLoadDefaultTableComplete
    } = useSelector(state => state.demandPlanning);

    useEffect(() => {
        return () => {
            dispatch(clearDemandPlanningTableData());
        }
    }, []);

    useEffect(() => {
        if (!overrideResetEpoch) return;
        setManualPercentById({});
    }, [overrideResetEpoch]);

    useEffect(() => {
        if (activeView?.id === "sku-allocation") {
            dispatch(getSkuAllocationTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "eligibility-validation") {
            dispatch(getEligibilityValidationTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "monthly-trend") {
            dispatch(getMonthlyTrendTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "historical") {
            dispatch(getHistoricalTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "forecast-vs-actuals") {
            dispatch(getForecastVsActualsTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "forecast-by-sku") {
            dispatch(getForecastBySkuTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "open-to-buy") {
            dispatch(getOpenToBuyTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
        if (activeView?.id === "overstock-inventory") {
            dispatch(getOverstockInventoryTableData({
                demandPlanningId: selectedForecastDemandPlanning?.id,
                demandPlanningYear: selectedForecastDemandPlanning?.forecast_year || selectedForecastDemandPlanning?.forecastYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            }));
        }
    }, [
        activeView,
        selectedForecastDemandPlanning,
        selectedCategoryId, 
        selectedSubcategoryId, 
        selectedStatusId,
        searchTerm,
    ]);

    const demandYearForTableLabels = useMemo(() => {
        const raw =
            selectedForecastDemandPlanning?.forecast_year ??
            selectedForecastDemandPlanning?.forecastYear;
        const y = Number.parseInt(String(raw ?? ""), 10);
        return Number.isFinite(y) ? y : null;
    }, [selectedForecastDemandPlanning]);

    /** SKU allocation monthly revenue columns reflect prior-year actuals (planning year − 1). */
    const skuAllocationMonthlyRevenueYear = useMemo(() => {
        if (demandYearForTableLabels == null) return null;
        return demandYearForTableLabels - 1;
    }, [demandYearForTableLabels]);

    const columnIds = useMemo(() => {
        let raw =
            activeView?.columnIds?.length
                ? activeView.columnIds
                : PRESET_COLUMN_IDS[activeView?.id] || [];
        if (
            activeView?.id === "monthly-trend" &&
            demandYearForTableLabels != null
        ) {
            raw = buildMonthlyTrendColumnIds();
        }
        if (
            activeView?.id === "forecast-vs-actuals" &&
            demandYearForTableLabels != null
        ) {
            raw = buildForecastVsActualsColumnIds();
        }
        const fromPreset = dedupeDemandPlanningColumnIds(raw);
        if (
            visibleColumnIdsProp === undefined ||
            visibleColumnIdsProp === null
        ) {
            return fromPreset;
        }
        const allow = new Set(visibleColumnIdsProp);
        return fromPreset.filter((id) => allow.has(id));
    }, [activeView, visibleColumnIdsProp, demandYearForTableLabels]);

    const visibleColumns = useMemo(
        () =>
            columnIds.map((id) => {
                let label = getDemandPlanningColumnLabel(id, {
                    viewId: activeView?.id,
                    planningYear: demandYearForTableLabels,
                });
                if (activeView?.id === "sku-allocation" && skuAllocationMonthlyRevenueYear != null) {
                    const monthly = getSkuAllocationMonthlyRevenueHeaderLabel(
                        id,
                        skuAllocationMonthlyRevenueYear
                    );
                    if (monthly) label = monthly;
                } else if (activeView?.id === "historical" && demandYearForTableLabels != null) {
                    const monthly = getSkuAllocationMonthlyRevenueHeaderLabel(id, demandYearForTableLabels);
                    if (monthly) label = monthly;
                }
                return {
                    id,
                    label,
                    width: defaultWidthForColumn(id),
                };
            }),
        [columnIds, activeView?.id, demandYearForTableLabels, skuAllocationMonthlyRevenueYear]
    );

    const stickyOffsets = useMemo(
        () => ({
            select: 0,
            supplySku: LEFT_COLUMN_WIDTHS.select,
        }),
        []
    );

    const tableMinWidth = useMemo(() => {
        const data = visibleColumns.reduce((sum, c) => sum + c.width, 0);
        return LEFT_COLUMN_WIDTHS.select + data;
    }, [visibleColumns]);

    const enrichedRecords = useMemo(() => {
        if (arrayHasItems(recordsProp)) return recordsProp;
        if (activeView?.id === "sku-allocation") {
            if (arrayHasItems(skuAllocationTableData)) return skuAllocationTableData;
            if (arrayHasItems(skuAllocationTableData?.items)) return skuAllocationTableData.items;
        }
        if (activeView?.id === "eligibility-validation") {
            if (arrayHasItems(eligibilityValidationTableData?.items)) return eligibilityValidationTableData.items;
            if (arrayHasItems(eligibilityValidationTableData)) return eligibilityValidationTableData;
        }
        if (activeView?.id === "monthly-trend") {
            if (arrayHasItems(monthlyTrendTableData?.items)) return monthlyTrendTableData.items;
            if (arrayHasItems(monthlyTrendTableData)) return monthlyTrendTableData;
        }
        if (activeView?.id === "historical") {
            if (arrayHasItems(historicalTableData?.items)) return historicalTableData.items;
            if (arrayHasItems(historicalTableData)) return historicalTableData;
        }
        if (activeView?.id === "forecast-vs-actuals") {
            if (arrayHasItems(forecastVsActualsTableData?.items)) return forecastVsActualsTableData.items;
            if (arrayHasItems(forecastVsActualsTableData)) return forecastVsActualsTableData;
        }
        if (activeView?.id === "forecast-by-sku") {
            if (arrayHasItems(forecastBySkuTableData?.items)) return forecastBySkuTableData.items;
            if (arrayHasItems(forecastBySkuTableData)) return forecastBySkuTableData;
        }
        if (activeView?.id === "open-to-buy") {
            if (arrayHasItems(openToBuyTableData?.items)) return openToBuyTableData.items;
            if (arrayHasItems(openToBuyTableData)) return openToBuyTableData;
        }
        if (activeView?.id === "overstock-inventory") {
            if (arrayHasItems(overstockInventoryTableData?.items)) return overstockInventoryTableData.items;
            if (arrayHasItems(overstockInventoryTableData)) return overstockInventoryTableData;
        }
        return [];
    }, [
        recordsProp,
        activeView?.id,
        skuAllocationTableData,
        eligibilityValidationTableData,
        monthlyTrendTableData,
        historicalTableData,
        forecastVsActualsTableData,
        forecastBySkuTableData,
        openToBuyTableData,
        overstockInventoryTableData,
    ]);

    const recordsWithManualPercents = useMemo(
        () =>
            enrichedRecords.map((record) => ({
                ...record,
                ...(manualPercentById[record.id] || {}),
            })),
        [enrichedRecords, manualPercentById]
    );

    const usesSkuAllocationDerived = viewUsesSkuAllocationDerivedChecks(activeView?.id);

    const allocationServerItems = useMemo(() => {
        const raw = skuAllocationTableData?.items;
        if (arrayHasItems(raw)) return raw;
        if (arrayHasItems(skuAllocationTableData)) return skuAllocationTableData;
        return [];
    }, [skuAllocationTableData]);

    const eligibilityValidationServerItems = useMemo(() => {
        const raw = eligibilityValidationTableData?.items;
        if (arrayHasItems(raw)) return raw;
        if (arrayHasItems(eligibilityValidationTableData)) return eligibilityValidationTableData;
        return [];
    }, [eligibilityValidationTableData]);

    const forecastBySkuServerItems = useMemo(() => {
        const raw = forecastBySkuTableData?.items;
        if (arrayHasItems(raw)) return raw;
        if (arrayHasItems(forecastBySkuTableData)) return forecastBySkuTableData;
        return [];
    }, [forecastBySkuTableData]);

    const openToBuyServerItems = useMemo(() => {
        const raw = openToBuyTableData?.items;
        if (arrayHasItems(raw)) return raw;
        if (arrayHasItems(openToBuyTableData)) return openToBuyTableData;
        return [];
    }, [openToBuyTableData]);

    const serverItemsForDerivedViews = useMemo(() => {
        if (activeView?.id === "forecast-by-sku") return forecastBySkuServerItems;
        if (activeView?.id === "open-to-buy") return openToBuyServerItems;
        if (activeView?.id === "eligibility-validation") return eligibilityValidationServerItems;
        return allocationServerItems;
    }, [
        activeView?.id,
        forecastBySkuServerItems,
        openToBuyServerItems,
        eligibilityValidationServerItems,
        allocationServerItems,
    ]);

    const subcategoryEligibleTotalsMap = useMemo(() => {
        if (!usesSkuAllocationDerived) return undefined;
        const tablePayload =
            activeView?.id === "eligibility-validation"
                ? eligibilityValidationTableData
                : activeView?.id === "forecast-by-sku"
                  ? forecastBySkuTableData
                  : activeView?.id === "open-to-buy"
                    ? openToBuyTableData
                    : skuAllocationTableData;
        return subcategoryEligibleTotalsMapFromTablePayload(tablePayload) ?? undefined;
    }, [
        usesSkuAllocationDerived,
        activeView?.id,
        skuAllocationTableData,
        eligibilityValidationTableData,
        forecastBySkuTableData,
        openToBuyTableData,
    ]);

    /** Apply pending override edits to full-catalog subcategory SUMIF totals until refetch completes. */
    const subcategoryEligibleTotalsMapForDerived = useMemo(() => {
        if (!subcategoryEligibleTotalsMap) return undefined;
        const pendingIds = Object.keys(manualPercentById);
        if (pendingIds.length === 0) return subcategoryEligibleTotalsMap;

        const map = new Map(subcategoryEligibleTotalsMap);
        for (const productId of pendingIds) {
            const overrides = manualPercentById[productId];
            const row = serverItemsForDerivedViews.find(
                (r) => String(r.id) === String(productId)
            );
            if (!row || !overrides) continue;

            const subKey = row.subCategory || "Unassigned";
            const oldEligible = computeEligibleFinalPercent(row);
            const newEligible = computeEligibleFinalPercent({ ...row, ...overrides });
            const deltaMicro =
                toEligibleAggregateMicro(newEligible) - toEligibleAggregateMicro(oldEligible);
            if (deltaMicro === 0) continue;

            const currentTotal = Number(map.get(subKey) || 0);
            const currentMicro = Math.round(currentTotal * PERCENT_AGGREGATE_SCALE + 1e-9);
            map.set(subKey, subcategoryEligibleTotalFromMicro(currentMicro + deltaMicro));
        }
        return map;
    }, [
        subcategoryEligibleTotalsMap,
        manualPercentById,
        serverItemsForDerivedViews,
    ]);

    const recordsForTable = useMemo(() => {
        if (usesSkuAllocationDerived) {
            return deriveSkuAllocationChecks(recordsWithManualPercents, {
                subcategoryEligibleTotalsMap: subcategoryEligibleTotalsMapForDerived,
            });
        }
        return recordsWithManualPercents;
    }, [usesSkuAllocationDerived, recordsWithManualPercents, subcategoryEligibleTotalsMapForDerived]);

    const tableRecords = useMemo(
        () => filterDemandPlanningTableRecordsBySearch(recordsForTable, searchTerm),
        [recordsForTable, searchTerm]
    );

    const tablePagination = useMemo(() => {
        const total = tableRecords.length;
        const totalPages = total === 0 ? 1 : Math.ceil(total / itemsPerPage);
        const page = Math.min(Math.max(1, currentPage), totalPages);
        const start = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
        const end = total === 0 ? 0 : Math.min(page * itemsPerPage, total);
        const rangeRows = total === 0 ? "0-0 of 0" : `${start}-${end} of ${total}`;
        return {
            currentPage: page,
            totalPages,
            rangeRows,
            isTheLastPage: page >= totalPages,
        };
    }, [tableRecords.length, currentPage, itemsPerPage]);

    const paginatedTableRecords = useMemo(() => {
        const page = tablePagination.currentPage;
        const start = (page - 1) * itemsPerPage;
        return tableRecords.slice(start, start + itemsPerPage);
    }, [tableRecords, tablePagination.currentPage, itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchTerm,
        activeView?.id,
        tableRecords.length,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    ]);

    useEffect(() => {
        tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPage]);

    useEffect(() => {
        if (currentPage > tablePagination.totalPages) {
            setCurrentPage(tablePagination.totalPages);
        }
    }, [currentPage, tablePagination.totalPages]);

    const visibleIds = useMemo(() => paginatedTableRecords.map((r) => r.id), [paginatedTableRecords]);

    const allVisibleSelected =
        visibleIds.length > 0 &&
        visibleIds.every((id) => selectedSkuIds.includes(id));
    const someVisibleSelected = visibleIds.some((id) =>
        selectedSkuIds.includes(id)
    );

    useEffect(() => {
        const el = headerCheckboxRef.current;
        if (el) {
            el.indeterminate = someVisibleSelected && !allVisibleSelected;
        }
    }, [someVisibleSelected, allVisibleSelected]);

    const toggleSelection = useCallback((id) => {
        setSelectedSkuIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    const toggleSelectAllVisible = useCallback(() => {
        setSelectedSkuIds((prev) => {
            if (allVisibleSelected) {
                const drop = new Set(visibleIds);
                return prev.filter((id) => !drop.has(id));
            }
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return Array.from(next);
        });
    }, [allVisibleSelected, visibleIds]);

    const selectedRecords = useMemo(
        () => tableRecords.filter((r) => selectedSkuIds.includes(r.id)),
        [tableRecords, selectedSkuIds]
    );
    const selectedCount = selectedRecords.length;

    const searchActive = String(searchTerm || "").trim().length > 0;
    const skuSummaryLine = useMemo(() => {
        const inView = recordsForTable.length;
        const shown = tableRecords.length;
        if (inView === 0) {
            return "No SKUs in this view.";
        }
        if (searchActive) {
            return `Showing ${shown} of ${inView} SKUs matching search.`;
        }
        return `Showing ${inView} SKU${inView === 1 ? "" : "s"} in this view.`;
    }, [recordsForTable.length, tableRecords.length, searchActive]);

    const updateManualPercent = useCallback((recordId, field, value) => {
        if (!MANUAL_PERCENT_COLUMNS.has(field)) return;
        setManualPercentById((prev) => {
            const current = prev[recordId] || {};
            return {
                ...prev,
                [recordId]: {
                    ...current,
                    [field]: value,
                },
            };
        });
    }, []);

    useEffect(() => {
        if (arrayHasItems(selectedSkuIds)) {
            setSelectedSkuIds([]);
        }
    }, [
        activeView?.id,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        itemsPerPage,
        currentPage,
    ]);

    const commitPercentField = useCallback(
        async (record, field, nextValue) => {
            if (!MANUAL_PERCENT_COLUMNS.has(field)) return;

            const normalizedNext =
                nextValue == null || nextValue === "" ? null : Number(nextValue);

            if (
                activeView?.id !== "sku-allocation" &&
                activeView?.id !== "forecast-by-sku" &&
                activeView?.id !== "open-to-buy" && 
                activeView?.id !== "eligibility-validation"
            ) {
                updateManualPercent(record.id, field, normalizedNext);
                return;
            }

            const planId = selectedForecastDemandPlanning?.id;
            const planYear =
                selectedForecastDemandPlanning?.forecast_year ??
                selectedForecastDemandPlanning?.forecastYear;
            if (planId == null || planYear === undefined || planYear === null || planYear === "") {
                return;
            }

            const baselineItems = serverItemsForDerivedViews;
            const serverRow = baselineItems.find((r) => r.id === record.id);
            const baseline = serverRow?.[field] ?? null;

            updateManualPercent(record.id, field, normalizedNext);

            if (percentValuesEqual(baseline, normalizedNext)) {
                return;
            }

            const refetchParams = {
                demandPlanningId: planId,
                demandPlanningYear: planYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            };

            try {
                await dispatch(
                    saveForecastDemandProductOverride({
                        demandPlanningId: planId,
                        demandPlanningYear: planYear,
                        productId: record.id,
                        field,
                        value: normalizedNext,
                    })
                ).unwrap();
                if (activeView?.id === "forecast-by-sku") {
                    await dispatch(getForecastBySkuTableData(refetchParams)).unwrap();
                } else if (activeView?.id === "open-to-buy") {
                    await dispatch(getOpenToBuyTableData(refetchParams)).unwrap();
                } else if (activeView?.id === "eligibility-validation") {
                    await dispatch(getEligibilityValidationTableData(refetchParams)).unwrap();
                } else {
                    await dispatch(getSkuAllocationTableData(refetchParams)).unwrap();
                }
                setManualPercentById((prev) => {
                    if (!prev[record.id]) return prev;
                    const next = { ...prev };
                    delete next[record.id];
                    return next;
                });
                toast.success("Override saved successfully.", { position: "bottom-right" });
            } catch (err) {
                setManualPercentById((prev) => {
                    if (!prev[record.id]) return prev;
                    const next = { ...prev };
                    delete next[record.id];
                    return next;
                });
                const msg =
                    typeof err === "string"
                        ? err
                        : err?.message || "Could not save override.";
                toast.error(msg, { position: "bottom-right" });
            }
        },
        [
            activeView?.id,
            serverItemsForDerivedViews,
            dispatch,
            selectedForecastDemandPlanning,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
            updateManualPercent,
        ]
    );

    const commitApprovedField = useCallback(
        async (record, nextValue) => {
            const approvedValue = normalizeApprovedSelectValue(nextValue);

            if (
                activeView?.id !== "forecast-by-sku" &&
                activeView?.id !== "open-to-buy"
            ) {
                return;
            }

            const planId = selectedForecastDemandPlanning?.id;
            const planYear =
                selectedForecastDemandPlanning?.forecast_year ??
                selectedForecastDemandPlanning?.forecastYear;
            if (planId == null || planYear === undefined || planYear === null || planYear === "") {
                return;
            }

            const baselineItems =
                activeView?.id === "open-to-buy" ? openToBuyServerItems : forecastBySkuServerItems;
            const serverRow = baselineItems.find((r) => r.id === record.id);
            const baselineApproved = normalizeApprovedSelectValue(serverRow?.approved);
            if (baselineApproved === approvedValue) {
                return;
            }

            const refetchParams = {
                demandPlanningId: planId,
                demandPlanningYear: planYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            };

            try {
                await dispatch(
                    saveForecastDemandProductApproval({
                        demandPlanningId: planId,
                        demandPlanningYear: planYear,
                        productId: record.id,
                        approved: approvedValue,
                    })
                ).unwrap();

                if (activeView?.id === "open-to-buy") {
                    await dispatch(getOpenToBuyTableData(refetchParams)).unwrap();
                } else {
                    await dispatch(getForecastBySkuTableData(refetchParams)).unwrap();
                }
                toast.success("Approval saved successfully.", { position: "bottom-right" });
            } catch (err) {
                const msg =
                    typeof err === "string"
                        ? err
                        : err?.message || "Could not save approval.";
                toast.error(msg, { position: "bottom-right" });
            }
        },
        [
            activeView?.id,
            dispatch,
            forecastBySkuServerItems,
            openToBuyServerItems,
            selectedForecastDemandPlanning,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
        ]
    );

    const commitApprovedNotesField = useCallback(
        async (record, nextValue) => {
            const nextNotes = String(nextValue ?? "").trim();
            if (!nextNotes) return;

            if (
                activeView?.id !== "forecast-by-sku" &&
                activeView?.id !== "open-to-buy"
            ) {
                return;
            }

            const planId = selectedForecastDemandPlanning?.id;
            const planYear =
                selectedForecastDemandPlanning?.forecast_year ??
                selectedForecastDemandPlanning?.forecastYear;
            if (planId == null || planYear === undefined || planYear === null || planYear === "") {
                return;
            }

            const baselineItems =
                activeView?.id === "open-to-buy" ? openToBuyServerItems : forecastBySkuServerItems;
            const serverRow = baselineItems.find((r) => r.id === record.id);
            const baselineNotes = String(serverRow?.notes ?? "").trim();
            if (baselineNotes === nextNotes) {
                return;
            }

            const refetchParams = {
                demandPlanningId: planId,
                demandPlanningYear: planYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            };

            try {
                await dispatch(
                    saveForecastDemandProductApprovedNotes({
                        demandPlanningId: planId,
                        demandPlanningYear: planYear,
                        productId: record.id,
                        approvedNotes: nextNotes,
                    })
                ).unwrap();

                if (activeView?.id === "open-to-buy") {
                    await dispatch(getOpenToBuyTableData(refetchParams)).unwrap();
                } else {
                    await dispatch(getForecastBySkuTableData(refetchParams)).unwrap();
                }
                toast.success("Notes saved successfully.", { position: "bottom-right" });
            } catch (err) {
                const msg =
                    typeof err === "string"
                        ? err
                        : err?.message || "Could not save notes.";
                toast.error(msg, { position: "bottom-right" });
            }
        },
        [
            activeView?.id,
            dispatch,
            forecastBySkuServerItems,
            openToBuyServerItems,
            selectedForecastDemandPlanning,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
        ]
    );

    const submitBulkApproval = useCallback(async () => {
        if (!pendingBulkApproval || selectedCount === 0) return;
        const approvedValue = pendingBulkApproval.value ? "true" : "false";
        const planId = selectedForecastDemandPlanning?.id;
        const planYear =
            selectedForecastDemandPlanning?.forecast_year ??
            selectedForecastDemandPlanning?.forecastYear;
        if (planId == null || planYear === undefined || planYear === null || planYear === "") {
            return;
        }

        setBulkApprovalSaving(true);
        try {
            await Promise.all(
                selectedRecords.map((record) =>
                    dispatch(
                        saveForecastDemandProductApproval({
                            demandPlanningId: planId,
                            demandPlanningYear: planYear,
                            productId: record.id,
                            approved: approvedValue,
                        })
                    ).unwrap()
                )
            );

            await dispatch(
                getOpenToBuyTableData({
                    demandPlanningId: planId,
                    demandPlanningYear: planYear,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                })
            ).unwrap();

            toast.success(
                `Updated approval status to "${pendingBulkApproval.label}" for ${selectedCount} product${selectedCount === 1 ? "" : "s"}.`,
                { position: "bottom-right" }
            );
            setSelectedSkuIds([]);
            setBulkApprovalConfirmOpen(false);
            setPendingBulkApproval(null);
        } catch (err) {
            const msg =
                typeof err === "string"
                    ? err
                    : err?.message || "Could not update bulk approval status.";
            toast.error(msg, { position: "bottom-right" });
        } finally {
            setBulkApprovalSaving(false);
        }
    }, [
        pendingBulkApproval,
        selectedCount,
        selectedForecastDemandPlanning,
        selectedRecords,
        dispatch,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    ]);

    const refetchAfterRollback = useCallback(async () => {
        const planId = selectedForecastDemandPlanning?.id;
        const planYear =
            selectedForecastDemandPlanning?.forecast_year ??
            selectedForecastDemandPlanning?.forecastYear;
        if (planId == null || planYear === undefined || planYear === null || planYear === "") {
            return;
        }

        const refetchParams = {
            demandPlanningId: planId,
            demandPlanningYear: planYear,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
        };

        if (activeView?.id === "forecast-by-sku") {
            await dispatch(getForecastBySkuTableData(refetchParams)).unwrap();
        } else if (activeView?.id === "open-to-buy") {
            await dispatch(getOpenToBuyTableData(refetchParams)).unwrap();
        } else if (activeView?.id === "eligibility-validation") {
            await dispatch(getEligibilityValidationTableData(refetchParams)).unwrap();
        } else {
            await dispatch(getSkuAllocationTableData(refetchParams)).unwrap();
        }
        setManualPercentById({});
    }, [
        activeView?.id,
        dispatch,
        selectedCategoryId,
        selectedForecastDemandPlanning,
        selectedStatusId,
        selectedSubcategoryId,
    ]);

    const renderColumnCell = (record, column, selected) => {
        const isSticky = column.id === "supplySku";
        const year =
            selectedForecastDemandPlanning?.forecast_year ||
            selectedForecastDemandPlanning?.forecastYear;
        const isMonthVarianceColumn = MONTH_TREND_MONTH_PREFIXES.some(
            (month) => column.id === `${month}${year}Variance`
        );
        if (column.id === "supplySku") {
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    left={stickyOffsets.supplySku}
                    sticky
                    width={column.width}
                    selected={selected}
                >
                    <span className="inline-flex overflow-hidden items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-[11px] font-medium">
                        {record.supplySku || "-"}
                    </span>
                </DataCellDefaultTable>
            );
        }

        if (column.id === "notes") {
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    left={
                        isSticky ? stickyOffsets.supplySku : undefined
                    }
                    sticky={isSticky}
                    width={column.width}
                    selected={selected}
                >
                    <input
                        className="px-2 py-1 w-full transition-all duration-200 rounded-md border border-gray-300 bg-white text-sm cursor-pointer"
                        placeholder="Add planner notes"
                        defaultValue={record.notes ?? ""}
                        onBlur={(event) => commitApprovedNotesField(record, event.target.value)}
                    />
                </DataCellDefaultTable>
            )
        }

        if (column.id === "approved") {
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    left={
                        isSticky ? stickyOffsets.supplySku : undefined
                    }
                    sticky={isSticky}
                    width={column.width}
                    selected={selected}
                >
                    <SelectDropdown
                        options={[
                            { label: "-", value: ""},
                            { label: "Approved", value: "true"},
                            { label: "Not Approved", value: "false"},
                        ]}
                        selectClassname="!bg-white !text-xs !shadow-none !p-2"
                        selectedItemValueClassname="!text-xs"
                        itemValueClassname="!text-xs !px-1.5 !py-1.5"
                        dropdownClassname="!bg-white !text-xs !shadow-none !p-1.5"
                        portal
                        value={normalizeApprovedSelectValue(record.approved)}
                        onChange={(value) => commitApprovedField(record, value)}
                    />
                </DataCellDefaultTable>
            )
        }

        if (
            column.id === `ytd${year}Variance` ||
            isMonthVarianceColumn
        ) {
            const display = formatPlanningCell(column.id, record[column.id]);
            const isNegativeValue = display.startsWith("-");
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    left={
                        isSticky ? stickyOffsets.supplySku : undefined
                    }
                    sticky={isSticky}
                    width={column.width}
                    selected={selected}
                >
                    <div className={`${isNegativeValue ? "!text-red-600 !font-medium" : ""}`}>
                        {display}
                    </div>
                </DataCellDefaultTable>
            )
        }

        if (column.id === "overstockLevel") {
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    left={
                        isSticky ? stickyOffsets.supplySku : undefined
                    }
                    sticky={isSticky}
                    width={column.width}
                    selected={selected}
                >
                    <div className={`inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md px-2 py-1 text-[11px] font-medium shadow-none border-transparent ${riskTone(record.overstockLevel)}`}>
                        {record.overstockLevel ? record.overstockLevel: "-"}
                    </div>
                </DataCellDefaultTable>
            )
        }
        
        if (column.id === "varianceFlag" || column.id === "riskFlag") {
            const value = column.id === "varianceFlag" ? record.varianceFlag : column.id === "riskFlag" ? record.riskFlag : "-";
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                    contentClassName="whitespace-normal overflow-visible"
                    disableTruncate
                >
                    <div className={`inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md px-2 py-1 text-[11px] font-medium shadow-none ${riskTone(value)}`}>
                        {value ?? "-"}
                    </div>
                </DataCellDefaultTable>
            )
        }

        if (column.id === "productName") {
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                    contentClassName="whitespace-normal overflow-visible"
                    disableTruncate
                >
                    <div>
                        <p className="font-medium text-gray-900">{record.productName || "-"}</p>
                        <p className="text-[11px] text-gray-500">{record.productLine || "-"}</p>
                    </div>
                </DataCellDefaultTable>
            );
        }

        if (column.id === "status") {
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                >
                    <InventoryStatusChip status={record.status} />
                </DataCellDefaultTable>
            );
        }

        if (column.id === "planned_gross_price") {
            const metadata = record.planned_gross_price_meta || {};
            const tooltip = [
                metadata.source ? `Source: ${metadata.source}` : null,
                metadata.version != null ? `Version: ${metadata.version}` : null,
                metadata.effective_from
                    ? `Effective: ${new Intl.DateTimeFormat("en-US").format(new Date(`${metadata.effective_from}T00:00:00Z`))}`
                    : null,
            ]
                .filter(Boolean)
                .join(" | ");
            const missing = metadata.status === "missing_governed";
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                >
                    <span title={tooltip || undefined} className="inline-flex items-center gap-1">
                        {formatPlanningCell(column.id, record[column.id])}
                        {missing ? (
                            <span className="rounded border border-amber-200 bg-amber-50 px-1 text-[10px] font-semibold text-amber-700">
                                Missing governed
                            </span>
                        ) : null}
                    </span>
                </DataCellDefaultTable>
            );
        }

        if (MANUAL_PERCENT_COLUMNS.has(column.id)) {
            const value = record[column.id] ?? null;
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                    editable={true}
                >
                    <PercentEntryInput
                        value={value}
                        onCommit={(nextValue) =>
                            commitPercentField(record, column.id, nextValue)
                        }
                    />
                </DataCellDefaultTable>
            );
        }

        if (VALIDATION_COLUMNS.has(column.id)) {
            const value = record[column.id] || "-";
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                >
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${validationTone(value)}`}>
                        {value}
                    </span>
                </DataCellDefaultTable>
            );
        }

        if (BOOLEAN_COLUMNS.has(column.id)) {
            const value = !!record[column.id];
            return (
                <DataCellDefaultTable
                    key={`${record.id}-${column.id}`}
                    width={column.width}
                    selected={selected}
                >
                    <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-700">
                        {value ? "TRUE" : "FALSE"}
                    </span>
                </DataCellDefaultTable>
            );
        }

        const display = formatPlanningCell(column.id, record[column.id]);
        return (
            <DataCellDefaultTable
                key={`${record.id}-${column.id}`}
                left={
                    isSticky ? stickyOffsets.supplySku : undefined
                }
                sticky={isSticky}
                width={column.width}
                selected={selected}
            >
                {display}
            </DataCellDefaultTable>
        );
    };

    const theadClasses = "sticky top-0 z-30";

    const skuAllocationTableDataLoading = loadingKeys.includes(DemandPlanningLoadingKeys.getSkuAllocationTableData);
    const eligibilityValidationTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getEligibilityValidationTableData
    );
    const monthlyTrendTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getMonthlyTrendTableData
    );
    const historicalTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getHistoricalTableData
    );
    const forecastVsActualsTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getForecastVsActualsTableData
    );
    const forecastBySkuTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getForecastBySkuTableData
    );
    const openToBuyTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getOpenToBuyTableData
    );
    const overstockInventoryTableDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getOverstockInventoryTableData
    );
    const tableDataLoading =
        activeView?.id === "sku-allocation"
            ? skuAllocationTableDataLoading
            : activeView?.id === "eligibility-validation"
              ? eligibilityValidationTableDataLoading
              : activeView?.id === "monthly-trend"
                ? monthlyTrendTableDataLoading
                : activeView?.id === "historical"
                  ? historicalTableDataLoading
                  : activeView?.id === "forecast-vs-actuals"
                    ? forecastVsActualsTableDataLoading
                    : activeView?.id === "forecast-by-sku"
                      ? forecastBySkuTableDataLoading
                      : activeView?.id === "open-to-buy"
                        ? openToBuyTableDataLoading
                        : activeView?.id === "overstock-inventory"
                          ? overstockInventoryTableDataLoading
                          : false;

    if (!firstLoadDefaultTableComplete && tableDataLoading) {
        return (
            <div className="flex justify-center items-center h-full mt-8">
                <LoaderSpinner
                    width="80"
                    height="80"
                    border="5"
                    colorSpinner="gray"
                />
            </div>
        )
    }

    if (!columnIds.length) {
        return (
            <div className="mx-5 rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
                No columns are configured for this planning view.
            </div>
        );
    }

    return (
        <>
            {arrayHasItems(selectedSkuIds) && activeView?.id !== "open-to-buy" && (
                <section className="top-[128px] z-20 border-b border-neutral-800 bg-neutral-950 py-2 text-white mb-4">
                    <div className="flex flex-wrap items-center gap-1.5 px-3 py-1 sm:flex-row flex-col">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between w-full">
                            <div>
                                <p className="text-sm font-medium">
                                    {selectedSkuIds.length} {selectedSkuIds.length === 1 ? "row" : "rows"} selected for governed bulk action
                                </p>
                                <p className="text-xs text-gray-300">
                                    Selected rows remain visible while you review downstream planning impact.
                                </p>
                            </div>
                            <div className="flex items-center gap-2 justify-center sm:justify-start flex-col sm:flex-row">
                                <button
                                    className="inline-flex items-center justify-center w-full sm:w-auto gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs dark:bg-transparent dark:border-input dark:hover:bg-input/50 h-9 px-4 py-2 has-[>svg]:px-3 rounded-md border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
                                    onClick={() => setSelectedSkuIds([])}
                                    disabled={!selectedSkuIds.length}
                                >
                                    Clear selection
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}
            {arrayHasItems(selectedSkuIds) && activeView?.id === "open-to-buy" && (
                <section className="top-[128px] z-20 border-b border-neutral-800 bg-neutral-950 py-2 text-white mb-4">
                    <div className="flex flex-wrap items-center gap-1.5 px-3 py-1 sm:flex-row flex-col">
                        <div className="text-xs font-semibold flex flex-row gap-1 items-center">
                            {selectedSkuIds.length} selected
                            <div className="mx-1 h-4 w-px bg-white/20" />
                        </div>
                        <div className="shrink-0">
                            <ProductCatalogBulkFieldMenu
                                key={`bulk-approved-${selectedCount}`}
                                triggerLabel="Approved"
                                menuTitle="Set Approval Status"
                                options={[
                                    { label: "Approved", value: "true" },
                                    { label: "Not Approved", value: "false" },
                                ]}
                                menuWidthClassName="w-52"
                                onSelect={(v) => {
                                    if (v !== "true" && v !== "false") return;
                                    const label = v === "true" ? "Approved" : "Not Approved";
                                    setPendingBulkApproval({
                                        label,
                                        value: v === "true",
                                    });
                                    setBulkApprovalConfirmOpen(true);
                                }}
                            />
                        </div>
                        <button
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:hover:bg-accent/50 py-2 has-[>svg]:px-3 h-8 rounded-md border-neutral-800 bg-primary px-2.5 text-xs text-white hover:bg-white/10 hover:text-white"
                            onClick={() => setSelectedSkuIds([])}
                            disabled={!selectedSkuIds.length}
                        >
                            Clear selection
                        </button>
                    </div>
                </section>
            )}
            <ConfrimModal
                isOpen={bulkApprovalConfirmOpen}
                isLoading={bulkApprovalSaving}
                title={
                    pendingBulkApproval
                        ? `Apply "${pendingBulkApproval.label}" to ${selectedCount} selected product${selectedCount === 1 ? "" : "s"}?`
                        : "Apply approval status?"
                }
                confirmButtonTitle="Yes"
                cancelClick={() => {
                    if (bulkApprovalSaving) return;
                    setBulkApprovalConfirmOpen(false);
                    setPendingBulkApproval(null);
                }}
                submitClick={submitBulkApproval}
                size="max-w-lg"
            />
            <SkuAllocationHistoryDrawer
                marginFromSidebar={marginFromSidebar}
                isOpen={historyDrawerOpen}
                onClose={() => onHistoryDrawerOpenChange?.(false)}
                demandPlanningId={selectedForecastDemandPlanning?.id}
                demandPlanningYear={
                    selectedForecastDemandPlanning?.forecast_year ??
                    selectedForecastDemandPlanning?.forecastYear
                }
                onRolledBack={refetchAfterRollback}
            />
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="max-h-[calc(100vh-13rem)] overflow-auto relative" ref={tableScrollRef}>
                    {tableDataLoading && firstLoadDefaultTableComplete && (
                        <div className="fixed inset-0 z-[200] bg-gray-400/50 flex items-center justify-center">
                            <LoaderSpinner width="100" height="100" border="6" colorSpinner="black" />
                        </div>
                    )}
                    <table
                        className="border-separate border-spacing-0 w-full"
                        style={{ minWidth: tableMinWidth }}
                    >
                        <thead className={theadClasses}>
                            <tr>
                                <HeaderCellDefaultTable
                                    left={stickyOffsets.select}
                                    sticky
                                    width={LEFT_COLUMN_WIDTHS.select}
                                >
                                    <input
                                        ref={headerCheckboxRef}
                                        type="checkbox"
                                        className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                        checked={allVisibleSelected}
                                        onChange={toggleSelectAllVisible}
                                        aria-label={
                                            allVisibleSelected
                                                ? "Deselect all visible rows"
                                                : "Select all visible rows"
                                        }
                                    />
                                </HeaderCellDefaultTable>
                                {visibleColumns.map((column) => (
                                    <HeaderCellDefaultTable
                                        key={column.id}
                                        left={
                                            column.id === "supplySku"
                                                ? stickyOffsets.supplySku
                                                : undefined
                                        }
                                        sticky={column.id === "supplySku"}
                                        width={column.width}
                                    >
                                        {column.label}
                                    </HeaderCellDefaultTable>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableRecords.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={visibleColumns.length + 1}
                                        className="border-b border-gray-100 px-3 py-10 text-center text-sm text-gray-500"
                                    >
                                        No SKUs match the current filters. Connect a
                                        data source or adjust search.
                                    </td>
                                </tr>
                            ) : (
                                paginatedTableRecords.map((record) => {
                                    const selected = selectedSkuIds.includes(
                                        record.id
                                    );
                                    return (
                                        <tr
                                            key={record.id}
                                            className={`transition-colors duration-75 hover:bg-gray-50/80 ${
                                                selected
                                                    ? "bg-gray-100"
                                                    : "bg-white"
                                            }`}
                                        >
                                            <DataCellDefaultTable
                                                left={stickyOffsets.select}
                                                selected={selected}
                                                sticky
                                                width={LEFT_COLUMN_WIDTHS.select}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                                    checked={selected}
                                                    onChange={() =>
                                                        toggleSelection(record.id)
                                                    }
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    aria-label={`Select row ${
                                                        record.supplySku ||
                                                        record.id
                                                    }`}
                                                />
                                            </DataCellDefaultTable>
                                            {visibleColumns.map((column) =>
                                                renderColumnCell(
                                                    record,
                                                    column,
                                                    selected
                                                )
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="mt-3 flex flex-col gap-3 px-5 text-xs text-gray-600 lg:flex-row lg:items-center lg:justify-between">
                <p>{skuSummaryLine}</p>
                <div className="flex shrink-0 flex-row flex-wrap items-center gap-2 sm:justify-end">
                    <p className="whitespace-nowrap text-center text-xs text-gray-500">
                        {tablePagination.rangeRows}
                    </p>
                    <p className="text-xs text-gray-500">Rows:</p>
                    <ProductCatalogPageSizeSelect
                        value={itemsPerPage}
                        options={PLANNING_PAGE_SIZE_OPTIONS}
                        dropUp
                        onChange={(next) => {
                            setCurrentPage(1);
                            setItemsPerPage(next);
                        }}
                        testId="default-planning-page-size"
                    />
                    <button
                        type="button"
                        disabled={(tablePagination.currentPage || 1) === 1}
                        onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <ChevronLeftIcon className="h-3 w-3" />
                    </button>
                    <p className="whitespace-nowrap text-center text-xs text-gray-500">
                        {`${tablePagination.currentPage || 1}/${tablePagination.totalPages || 1}`}
                    </p>
                    <button
                        type="button"
                        disabled={tablePagination.isTheLastPage}
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <ChevronRightIcon className="h-3 w-3" />
                    </button>
                </div>
                <p className="lg:text-right">
                    {selectedRecords.length > 0
                        ? `${selectedRecords.length} visible selected rows ready for governed bulk actions.`
                        : "No rows selected."}
                </p>
            </div>
        </>
    );
};

export default DefaultPlanningTable;
